// Practice Project Statistics Component
// Date: January 21, 2026

import { Card, CardContent } from '../ui/card';
import { BookOpen, CheckCircle, Users, TrendingUp, Clock } from 'lucide-react';
import { usePracticeProjectStatsQuery } from '../../api/practiceProjectsApi';
import { Skeleton } from '../ui/skeleton';

interface DetailedStatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  variant?: 'default' | 'success' | 'warning' | 'info';
}

// Detailed stat card for Practice Projects (extends basic StatCard with icon, description, variant)
function DetailedStatCard({ title, value, icon, description, variant = 'default' }: DetailedStatCardProps) {
  const variantStyles = {
    default: 'bg-blue-50 text-blue-600 border-blue-200',
    success: 'bg-green-50 text-green-600 border-green-200',
    warning: 'bg-orange-50 text-orange-600 border-orange-200',
    info: 'bg-purple-50 text-purple-600 border-purple-200',
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-full ${variantStyles[variant]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
          </div>
          <Skeleton className="h-12 w-12 rounded-full" />
        </div>
      </CardContent>
    </Card>
  );
}

interface PracticeProjectStatsProps {
  variant?: 'admin' | 'student';
}

export function PracticeProjectStats({ variant = 'admin' }: PracticeProjectStatsProps) {
  const { data, isLoading, isError } = usePracticeProjectStatsQuery();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Failed to load statistics</p>
        </CardContent>
      </Card>
    );
  }

  const stats = data.data;

  if (variant === 'student') {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <DetailedStatCard
          title="Total Projects"
          value={stats.publishedProjects}
          icon={<BookOpen className="w-6 h-6" />}
          variant="default"
        />
        <DetailedStatCard
          title="My Enrollments"
          value={stats.totalEnrollments}
          icon={<Users className="w-6 h-6" />}
          variant="info"
        />
        <DetailedStatCard
          title="In Progress"
          value={stats.activeEnrollments}
          icon={<Clock className="w-6 h-6" />}
          variant="warning"
        />
        <DetailedStatCard
          title="Completed"
          value={stats.completedEnrollments}
          icon={<CheckCircle className="w-6 h-6" />}
          variant="success"
        />
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <DetailedStatCard
        title="Total Projects"
        value={stats.totalProjects}
        icon={<BookOpen className="w-6 h-6" />}
        description={`${stats.publishedProjects} published`}
        variant="default"
      />
      <DetailedStatCard
        title="Published"
        value={stats.publishedProjects}
        icon={<CheckCircle className="w-6 h-6" />}
        description={`${stats.draftProjects} drafts`}
        variant="success"
      />
      <DetailedStatCard
        title="Total Enrollments"
        value={stats.totalEnrollments}
        icon={<Users className="w-6 h-6" />}
        description={`${stats.activeEnrollments} active`}
        variant="info"
      />
      <DetailedStatCard
        title="Avg. Progress"
        value={`${Math.round(stats.averageCompletionPercentage)}%`}
        icon={<TrendingUp className="w-6 h-6" />}
        description={`${stats.completedEnrollments} completed`}
        variant="warning"
      />
    </div>
  );
}
